<template>
  <div>
    Envía mensajes predefinidos por WhatsApp usando plantillas personalizados con o sin parámetros.
  </div>
  <p class="campaign_name">Estás en la campaña: {{ sequence?.portfolio?.name }}</p>
  <a-form layout="vertical" ref="formRef" :model="formState" :rules="rules">
    <p class="title">Configura tu nodo</p>
    <a-form-item name="channel_customer">
      <template #label>
        <div>
          <span>Identificador a utilizar</span>
        </div>
      </template>
      <a-select
        v-model:value="formState.channel_customer"
        :getPopupContainer="(triggerNode: any) => triggerNode.parentNode"
        placeholder="Seleccionar identificar"
        :disabled="disabled"
        @change="handleChangeIdentifier"
      >
        <a-select-option v-for="identifier in whatsappIdentifiers" :key="identifier.id">
          {{ identifier.alias }} {{ identifier.associate ? `(${identifier.associate})` : "" }}
        </a-select-option>
      </a-select>
    </a-form-item>
    <queue-picker
      :name="'queue'"
      :value="formState.queue"
      :portfolio="sequence?.portfolio?.id"
      :showAll="false"
      :showTooltip="false"
      :disabled="disabled || !formState.channel_customer"
      :channelCustomer="formState.channel_customer"
      :placeholder="!formState.channel_customer ? 'Seleccionar un identificador' : 'Seleccionar cola'"
      help="Se listarán aquellas colas con plantillas activas."
      @update:value="handleSelectQueue"
      @change="handleChangeQueue"
      required
    >
      <template #label>
        <div class="label">
          <span class="label_info">
            <span>Cola</span>
            <a-tooltip title="La cola seleccionada no solo filtra las plantillas, sino que también será donde será creado el ticket generado por la secuencia.">
              <InfoCircleOutlined />
            </a-tooltip>
          </span>
          <a-button
            type="link"
            :disabled="!formState.queue || disabled"
            @click="showModalUsers = true"
          >
            Ver usuarios asignados
          </a-button>
        </div>
      </template>
    </queue-picker>
    <a-form-item name="whatsapp_template" required>
      <template #label>
        <div class="label">
          <span>Plantilla de whatsapp</span>
          <a-button
            type="link"
            @click="showModal = true"
            :disabled="!formState.whatsapp_template || disabled"
          >
            Previsualiza tu plantilla
          </a-button>
        </div>
      </template>

      <a-select
        v-model:value="formState.whatsapp_template"
        :disabled="!formState.queue || disabled || templatesWhatsapp.length == 0"
        :placeholder="
          !formState.queue ? 'Seleccionar una cola' : 
          (templatesWhatsapp.length > 0
            ? 'Seleccionar plantilla de Whatsapp'
            : 'No se encontraron plantillas en esta cola, crea tu primera plantilla')
        "
        @change="changeDisabledButton(); changeTemplate()"
        :getPopupContainer="(triggerNode: any) => triggerNode.parentNode"
        :option-label-prop="'customLabel'"
      >
        <a-select-opt-group
          label="Sin parámetros"
          v-if="templatesWhatsappWithoutParameters.length > 0"
        >
          <a-select-option
            v-for="template in templatesWhatsappWithoutParameters"
            :key="template.id"
            :customLabel="template.name"
          >
            {{ template.name }}
          </a-select-option>
        </a-select-opt-group>
        <a-select-opt-group
          label="Con parámetros"
          v-if="templatesWhatsappWithParameters.length > 0"
        >
          <a-select-option
            v-for="template in templatesWhatsappWithParameters"
            :key="template.id"
            :customLabel="`${template.name} (con parámetros)`"
          >
            {{ template.name }}
          </a-select-option>
        </a-select-opt-group>
      </a-select>
      <span class="text-whatsapp-template"
        >Se listarán aquellas plantillas activas de la cola seleccionada.</span
      >
    </a-form-item>
    <a-form-item v-if="currentTemplate?.template_type && currentTemplate?.template_type !== WhatsappTemplateTypes.Text" name="media" required>
      <div class="card">
        <h4>
          {{ getTemplateTypeText(templateType) || "Archivo" }} adjunto
        </h4>
        <div class="container__archive">
          <archive-picker
            v-model:value="formState.media"
            :supported-types="supportedTypes"
            :maxMBSize="maxMBSize"
            :disabled="disabled"
          />
        </div>
      </div>
    </a-form-item>
    <div v-if="currentTemplate?.parameters?.length > 0">
      <template-parameters
        v-model:values="formState.parameters"
        :form="formRef"
        :template="currentTemplate"
        :portfolioId="sequence?.portfolio?.id"
        :disabled="disabled"
      />
    </div>
    <p class="title">Defina el siguiente paso</p>
    <ActionPicker
      v-if="formState.action"
      v-model:value="formState.action"
      :disabled="disabled"
      :isMultichannel="true"
      :sequence="sequence"
      @update:value="changeDisabledButton"
    />
  </a-form>

  <a-modal
    :title="`Plantilla ${currentTemplate?.name}`"
    class="modal-template"
    v-model:open="showModal"
    width="500px"
    :bodyStyle="{ height: '580px', justifyContent: 'center', display: 'flex' }"
    centered
  >
    <div class="container-template">
      <WhatsappTemplatePhone
        v-if="currentTemplate"
        :content="currentTemplate?.template"
        :templateType="templateType"
        :buttons="buttons"
      />
    </div>
    <template #footer>
      <a-divider />
      <a-button @click="showModal = false" class="btn-large"> Cerrar previsualización </a-button>
    </template>
  </a-modal>

  <!-- @cancel="cancelUsersQueue" -->
  <a-modal
    :title="`Editar usuarios de la cola`"
    v-if="showModalUsers"
    v-model:open="showModalUsers"
    width="728px"
    :bodyStyle="{ height: '550px' }"
    centered
  >
    <StepUserSelection
      ref="userSelection"
      isEdit
      :isActive="true"
      :idPortfolio="sequence?.portfolio?.id"
      :idQueue="formState.queue"
    />
    <template #footer>
      <a-divider />
      <a-button @click="cancelUsersQueue"> Cancelar </a-button>
      <a-button @click="saveUsers" type="primary"> Guardar usuarios </a-button>
    </template>
  </a-modal>
</template>

<script setup lang="ts">
import { ref, onMounted, computed, createVNode, watch, Ref, inject } from "vue";
import { FormInstance } from "ant-design-vue";

import { SequenceNode, Sequence } from "@/@types/flows/sequences";
import { WhatsappTemplateTypes } from "@/app/channels/utils/enums";

import QueuePicker from "@/app/shared/components/pickers/QueuePicker.vue";
import ActionPicker from "@/app/flows/sequences/components/pickers/ActionPicker.vue";
import { WppTemplate } from "@/@types/global";
import { fetchTemplates } from "@/app/workspace/services";
import ArchivePicker from "@/app/shared/components/molecules/ArchivePicker.vue";

import WhatsappTemplatePhone from "@/app/shared/components/organisms/WhatsappTemplatePhone.vue";
import StepUserSelection from "@/app/flows/sequences/components/sections/drawerForms/StepUserSelection.vue";
import { updateQueue } from "@/app/queues/services";
import { message, Modal } from "ant-design-vue";
import { ResponseError } from "@/app/shared/utils/httpManager";
import { QuestionCircleOutlined, InfoCircleOutlined } from "@ant-design/icons-vue";
import { ChannelType } from "@/app/shared/utils/enums";
import TemplateParameters from "@/app/flows/sequences/components/sections/drawerForms/TemplateParameters.vue";

const props = defineProps<{ node: any; disabled: boolean; sequence: Sequence }>();

const formRef = ref<FormInstance>();
const formState = ref<Partial<SequenceNode>>({
  action: props.node.action,
  queue: props.node?.queue?.id,
  channel_customer: props.node?.channel_customer?.id,
  whatsapp_template: props.node?.whatsapp_template?.id,
  media: props.node?.media_url || undefined,
  parameters: props.node?.parameters ?? [],
});

watch(
  () => props.node,
  () => {
    formState.value = {
      action: props.node.action,
      queue: props.node?.queue?.id,
      channel_customer: props.node?.channel_customer?.id,
      media: props.node?.media_url || undefined,
      whatsapp_template: props.node?.whatsapp_template?.id,
      parameters: props.node?.parameters ?? [],
    };
  }
);

const whatsappIdentifiers = computed(() => {
  return props.sequence?.channels_customers?.filter((el) => el.channel?.id == ChannelType.Whatsapp);
});

const rules = {
  channel_customer: [{ required: true, message: "Campo requerido" }],
  queue: [{ required: true, message: "Campo requerido" }],
  whatsapp_template: [{ required: true, message: "Campo requerido" }],
  parameters: [{ required: true }],
};

const getTemplateTypeText = (type: string) => {
  let text = "";

  if (type === WhatsappTemplateTypes.TextAndImage) text = "Imagen";
  if (type === WhatsappTemplateTypes.TextAndVideo) text = "Video";
  if (type === WhatsappTemplateTypes.TextAndDocument) text = "Documento";

  return text;
};

const supportedTypes = computed(() => {
  let types: string[] = [];

  if (currentTemplate.value?.template_type === WhatsappTemplateTypes.TextAndImage)
    types = [".png", ".jpg", ".jpeg"];
  if (currentTemplate.value?.template_type === WhatsappTemplateTypes.TextAndVideo)
    types = [".mp4"];
  if (currentTemplate.value?.template_type === WhatsappTemplateTypes.TextAndDocument)
    types = [".pdf"];

  return types;
});

const maxMBSize = computed(() => {
  let size = null;

  if (currentTemplate.value?.template_type === WhatsappTemplateTypes.TextAndImage) size = 2;
  if (currentTemplate.value?.template_type === WhatsappTemplateTypes.TextAndVideo) size = 5;
  if (currentTemplate.value?.template_type === WhatsappTemplateTypes.TextAndDocument) size = 5;

  return size;
});

const showModal = ref(false);
const showModalUsers = ref(false);
// const queues = ref([]);
const templatesWhatsapp = ref<WppTemplate[]>([]);
const userSelection = ref();
const saveDisabled = inject("saveDisabled") as Ref<Boolean>;

const currentTemplate = computed(() => {
  return templatesWhatsapp.value.find(
    (template) => template.id === formState.value?.whatsapp_template
  );
});

watch(
  () => currentTemplate.value,
  (value) => {
    if (!value?.parameters || value?.parameters?.length == 0) {
      formState.value.parameters = undefined;
    }
  }
);

const templatesWhatsappWithParameters = computed(() => {
  return templatesWhatsapp.value.filter((template) => template.parameters?.length > 0);
});

const templatesWhatsappWithoutParameters = computed(() => {
  return templatesWhatsapp.value.filter((template) => template.parameters === null);
});

const templateType = computed(() => {
  return currentTemplate.value?.template_type || WhatsappTemplateTypes.Text;
});

const buttons = computed(() => {
  return currentTemplate.value?.buttons ?? [];
});

const getValidatedFormState = async () => {
  await formRef.value?.validateFields();

  return formState.value;
};

const handleSelectQueue = async (value: number) => {
  formState.value.queue = value;
  await fetchTemplatesWhatsapp();
};

const fetchTemplatesWhatsapp = async () => {
  if (formState.value.queue) {
    // formState.whatsapp_template = null;
    templatesWhatsapp.value = [...(await fetchTemplates(formState.value.queue, formState.value.channel_customer, false))];
  }
};

const saveUsers = async () => {
  try {
    await updateQueue(
      formState.value.queue,
      {
        users: userSelection.value.selectedItems.map((el: any) => ({ id: el })),
      },
      true
    );
    message.success(`Se ha actualizado la cola correctamente`);
    showModalUsers.value = false;
  } catch (error) {
    let errorMessage = "Ha ocurrido un error";
    if (error instanceof ResponseError) errorMessage = error.getErrorMessage();

    message.error(errorMessage);
  }
};

const cancelUsersQueue = () => {
  Modal.confirm({
    icon: createVNode(QuestionCircleOutlined),
    title: "¿Estás seguro de cancelar la edición de usuarios de la cola?",
    content: "No se guardarán los cambios realizados",
    okText: "Aceptar",
    cancelText: "Cancelar",
    centered: true,
    onOk: async () => {
      showModalUsers.value = false;
    },
  });
};

const changeDisabledButton = () => {
  saveDisabled.value = false;
};

const handleChangeIdentifier = () => { 
  formState.value.queue = undefined;
  formState.value.whatsapp_template = undefined;
  formState.value.parameters = [];
  changeDisabledButton();
}

const handleChangeQueue = () => {
  formState.value.whatsapp_template = undefined;
  formState.value.parameters = [];
  changeDisabledButton();
};

const changeTemplate = () => {
  formState.value.media = undefined;
}

watch(
  () => formState.value,
  (lastValue, newValue) => {
    if (lastValue && newValue) {
      saveDisabled.value = false;
    }
  },
  { deep: true, immediate: true }
);

defineExpose({ getValidatedFormState });

onMounted(async () => {
  // currentTemplate.value = templatesWhatsapp.value.find(
  //   (template) => template.id === formState.template
  // );
});
</script>

<style scoped lang="sass">
p
  color: $text-primary
  margin-bottom: 24px

h2
  color: $text-primary
  font-size: 16px
  font-weight: 600
  margin-bottom: 12px

.picker
  width: 100%

:deep(.ant-form-item-label > label)
  width: 100%
:deep(.ant-form-item-label > label > div)
  display: flex
  justify-content: space-between
  align-items: center
  width: 100%

.container-template
  width: 290px
  margin-top: 20px

:global(.modal-template .ant-modal-footer)
  text-align: center
.btn-large
    width: 350px
.campaign_name
  margin-top: 8px
  margin-bottom: 16px
  font-size: 13px
  font-style: italic
  color: #1677FF
.title
  font-size: 15px
  font-weight: bold
.text-whatsapp-template
  color: $gray-7

:deep(.ant-form-item-label)
  width: 100%
:deep(label[for="form_item_queue"])
  width: 100%
:deep(label[for="form_item_whatsapp_template"])
  width: 100%
.label
  width: 100%
  display: flex
  justify-content: space-between
  &_info
    display: flex
    gap: 4px
</style>
